// SCR-11 roles & permissions (plan 7.4, 8.4.11): matrix (35 keys × roles), grants, 2-person approvals,
// investigation cases, service accounts, quarterly access review. Every check below is enforced by the server:
// self_grant · permission_not_subset · scope_exceeds_grantor · target_more_privileged · last_admin (409) · duplicate.
import { z } from 'zod';
import { ApprovalKind, ApprovalStatus, CaseStatus, ReasonCategory } from '../enums.js';
import { PERMISSION_BUNDLES, PermissionDef, PermissionKey, PermissionLevel, PermissionMap } from '../permissions.js';
import { RoleKind } from '../roles-menus.js';
import { Count, EpochMs, Id, LocalizedText, PageMeta, PageQuery, PersonRef, RoleId, RoleRef, TeamId, TeamRef, UndoTicket, UserId, Ymd } from '../common.js';

export const RoleParams = z.object({ roleId: RoleId });
export const RolePermissionParams = z.object({ roleId: RoleId, key: PermissionKey });

export const RoleView = z.object({
  id: RoleId,
  kind: RoleKind,
  name: LocalizedText.nullable(),
  desc: LocalizedText.nullable(),
  basedOn: RoleId.nullable(),
  /** Base roles are read-only (copy to edit). */
  readOnly: z.boolean(),
  perms: PermissionMap,
  permCount: Count,
  mfaRequired: z.boolean(),
  pledgeRequired: z.boolean(),
  /** logs.prompt/answer at team+ ("글을 볼 수 있는 역할"). */
  isContentRole: z.boolean(),
  /** Grants of this role need an expiry date (text/work keys at team+). */
  needsExpiry: z.boolean(),
  /** Any key at 'team' → grants need applied teams. */
  needsTeams: z.boolean(),
  /** null = everyone (member). */
  holdersCount: Count.nullable(),
  holdersPreview: z.array(PersonRef).max(3),
  /** Holders whose effective permissions also include another role. */
  otherRoleHoldersCount: Count,
  bundles: z.record(z.enum(PERMISSION_BUNDLES), z.object({ max: PermissionLevel, on: Count, total: Count })),
  createdAt: EpochMs.nullable(),
  createdBy: PersonRef.nullable(),
});
export type RoleView = z.infer<typeof RoleView>;

/** GET /api/v1/admin/roles — matrix data + what the viewer may change. */
export const RolesResponse = z.object({
  roles: z.array(RoleView),
  permissionDefs: z.array(PermissionDef),
  /** 'roleId:key' cells that never change (base roles, last-admin keys). */
  locked: z.array(z.string()),
  pendingCells: z.array(z.object({ roleId: RoleId, key: PermissionKey, to: PermissionLevel, approvalId: Id })),
  viewer: z.object({
    /** Company-wide roles.manage edits definitions; team scope only grants/revokes in its teams. */
    canEditDefinitions: z.boolean(),
    heldRoleIds: z.array(RoleId),
    /** Viewer's effective permissions = the ceiling of what they may grant. */
    limits: PermissionMap,
    scopeTeams: z.array(TeamRef),
  }),
  kpis: z.object({ byKind: z.record(RoleKind, Count), contentRoles: z.array(RoleId), pendingApprovals: Count, grantsNeedingExpiry: Count, grantsMissingExpiry: Count }),
  lastChange: z.object({ at: EpochMs, actor: PersonRef.nullable() }).nullable(),
});
export type RolesResponse = z.infer<typeof RolesResponse>;

/** POST /api/v1/admin/roles — copy a role (custom). Name 2–20 chars, unique in ko/en. */
export const RoleCreateRequest = z.object({ name: z.string().trim().min(2).max(20), desc: z.string().trim().max(80).optional(), basedOn: RoleId });
export const RoleCreateResponse = z.object({ role: RoleView, undo: UndoTicket });
export const RoleUpdateRequest = z.object({ name: z.string().trim().min(2).max(20).optional(), desc: z.string().trim().max(80).optional() });

/**
 * PUT /api/v1/admin/roles/:roleId/permissions/:key — one cell. `from` must equal the stored value (409 stale_version).
 * Raising a dual-approval key creates an approval (reason ≥ 10) instead of applying.
 */
export const RolePermissionChangeRequest = z.object({ from: PermissionLevel, to: PermissionLevel, reason: z.string().trim().max(200).optional() });

/**
 * A 2-person approval (plan 7.4.2). `code` is the display code ("APR-0031"); `id` is opaque and never shown.
 * `manager_team_content` = turning on a manager's team text view (plan 7.3.3).
 */
export const ApprovalRow = z.object({
  id: Id,
  code: z.string().regex(/^APR-\d{4,}$/),
  kind: ApprovalKind,
  status: ApprovalStatus,
  requester: PersonRef,
  requestedAt: EpochMs,
  /** Deadline of the request itself (separate from the grant's own expiry). */
  requestExpiresAt: EpochMs,
  reason: z.string(),
  target: z.object({ person: PersonRef.nullable(), roleId: RoleId.nullable(), caseId: Id.nullable(), invite: z.object({ id: Id, emailMasked: z.string().max(254) }).optional() }),
  /** Distinguishes additive grants from replacing all additional roles; null for other approval kinds. */
  grantOperation: z.enum(['add', 'replace', 'team_managers']).nullable().default(null),
  managerChange: z.object({ team: TeamRef, added: z.array(PersonRef), removed: z.array(PersonRef) }).optional(),
  /** Changed cells, including reductions when replacing roles. */
  changes: z.array(z.object({ key: PermissionKey, from: PermissionLevel, to: PermissionLevel })),
  teams: z.array(TeamRef),
  grantExpiresAt: EpochMs.nullable(),
  notifyDeferredUntil: EpochMs.nullable(),
  /** Approval changes the notice's "who can view" section → a new notice draft is created. */
  noticeImpact: z.boolean(),
  approverRoles: z.array(RoleId),
  decidedBy: PersonRef.nullable(),
  decidedAt: EpochMs.nullable(),
  decisionReason: z.string().nullable(),
  applied: z.boolean(),
  canDecide: z.boolean(),
  canWithdraw: z.boolean(),
  /**
   * Role grants only: nobody but the requester could ever decide (no other active account with access.approve 'all'
   * that is not the target), so the requester may approve it — recorded as such. A second approver ends this.
   */
  soleApprover: z.boolean().optional(),
});
export type ApprovalRow = z.infer<typeof ApprovalRow>;

export const RolePermissionChangeResponse = z.object({
  applied: z.boolean(),
  role: RoleView.nullable(),
  approval: ApprovalRow.nullable(),
  /** Holders whose sessions are revoked immediately. Undo restores permissions, never revoked sessions. */
  sessionsRevoked: Count,
  noticeDraftCreated: z.boolean(),
  /** Applied changes can be undone for a few seconds ("되돌리기"); null when an approval was created instead. */
  undo: UndoTicket.nullable(),
});
/** Role delete, grant revoke: done at once, undoable for a few seconds (restores the exact prior state). */
export const UndoableOkResponse = z.object({ ok: z.literal(true), undo: UndoTicket });

export const GRANT_BLOCKS = ['last_admin', 'self', 'target_more_privileged', 'scope'] as const;
export const GrantRow = z.object({
  id: Id,
  person: PersonRef,
  role: RoleRef,
  teams: z.array(TeamRef),
  grantedAt: EpochMs,
  grantedBy: PersonRef.nullable(),
  approvedBy: PersonRef.nullable(),
  expiresAt: EpochMs.nullable(),
  reason: z.string().nullable(),
  expiryMissing: z.boolean(),
  expiryDeferred: z.boolean().default(false),
  daysLeft: z.int().nullable(),
  companyWide: z.boolean(),
  revoke: z.object({ ok: z.boolean(), block: z.enum(GRANT_BLOCKS).nullable() }),
});
export type GrantRow = z.infer<typeof GrantRow>;
export const GrantListQuery = z.object({ q: z.string().trim().max(60).optional(), roleId: RoleId.optional(), ...PageQuery.shape });
export const GrantListResponse = z.object({ items: z.array(GrantRow), page: PageMeta });

/**
 * Person picker of the grant form: everyone in the viewer's scope up front (name order), narrowed by `q` (names per
 * viewer permission, pseudonyms otherwise). Counts feed "n명 · 고를 수 있는 사람 m명".
 */
export const GrantCandidatesQuery = z.object({ q: z.string().trim().max(60).optional(), roleId: RoleId.optional(), ...PageQuery.shape });
export const GrantCandidatesResponse = z.object({
  items: z.array(z.object({ person: PersonRef, team: TeamRef.nullable(), roles: z.array(RoleRef), selectable: z.boolean(), block: z.enum(['self', 'target_more_privileged', 'scope']).nullable() })),
  page: PageMeta,
  counts: z.object({ total: Count, selectable: Count }),
});

/** POST /api/v1/admin/grants/preview — dry run: which keys rise, does it need approval, what is missing. */
export const GrantPreviewRequest = z.object({ userId: UserId, roleId: RoleId, teamIds: z.array(TeamId).default([]) });
export const GrantPreviewResponse = z.object({
  raised: z.array(z.object({ key: PermissionKey, from: PermissionLevel, to: PermissionLevel, dual: z.boolean() })),
  /** Also required for a new wide sensitive grant already covered by another role, whose lifetime may differ. */
  requiresApproval: z.boolean(),
  needsTeams: z.boolean(),
  needsExpiry: z.boolean(),
  exceedsViewer: z.boolean(),
  alreadyHeld: z.boolean(),
});

/** POST /api/v1/admin/grants — reason ≥ 5 (≥ 10 when an approval is created); expiry required for text roles. */
export const GrantCreateRequest = z.object({
  userId: UserId,
  roleId: RoleId,
  teamIds: z.array(TeamId).default([]),
  expiresAt: Ymd.nullable().default(null),
  reason: z.string().trim().min(5).max(200),
});
export const GrantCreateResponse = z.object({ applied: z.boolean(), grant: GrantRow.nullable(), approval: ApprovalRow.nullable(), sessionsRevoked: Count, undo: UndoTicket.nullable() });
export const GrantParams = z.object({ grantId: Id });
export const GrantRevokeRequest = z.object({ reason: z.string().trim().max(200).optional() });

export const ApprovalListQuery = z.object({ status: z.enum(['pending', 'done', 'all']).default('pending') });
export const ApprovalListResponse = z.object({ items: z.array(ApprovalRow) });
export const ApprovalParams = z.object({ approvalId: Id });
/**
 * Approve and reject both carry a reason into the log (plan 7.4.2 "승인·거절 모두 사유와 함께"); reject ≥ 5 chars
 * (shown to the requester). Requester ≠ approver (403 requester_cannot_approve). The mockup's optional approve memo
 * and one-click reject on the home card follow this rule instead (BUILD-SPEC-api API-32).
 */
export const ApprovalApproveRequest = z.object({ reason: z.string().trim().min(1).max(200) });
export const ApprovalRejectRequest = z.object({ reason: z.string().trim().min(5).max(200) });
/** POST …/withdraw by the requester: undoable for a few seconds (the request comes back as it was). */
export const ApprovalWithdrawResponse = z.object({ approval: ApprovalRow, undo: UndoTicket });

/** Investigation cases (plan 7.5.3): targets × period (≤ 30 days) × reason; start only after 2-person approval. */
export const CaseRow = z.object({
  id: Id,
  /** Display code ("INV-2026-004"). */
  code: z.string().regex(/^INV-\d{4}-\d{3,}$/),
  title: z.string(),
  reasonCategory: ReasonCategory,
  description: z.string(),
  requester: PersonRef,
  approver: PersonRef.nullable(),
  targets: z.array(PersonRef),
  from: Ymd,
  to: Ymd,
  createdAt: EpochMs,
  approvedAt: EpochMs.nullable(),
  /** approved with `to` passed is reported as closed. */
  status: CaseStatus,
  notifyDeferredUntil: EpochMs.nullable(),
  views: Count,
  closedAt: EpochMs.nullable(),
});
/** Cases visible to the viewer: all with company scope; team scope sees cases whose targets are in its teams. */
export const CaseListResponse = z.object({ items: z.array(CaseRow) });
/** Investigation targets: identity-safe names/search, independent of role-grant authority. */
export const CaseCandidatesQuery = z.object({ q: z.string().trim().max(60).optional(), ...PageQuery.shape });
export const CaseCandidatesResponse = z.object({ items: z.array(PersonRef), page: PageMeta });
/** POST …/close: undoable for a few seconds (reopens as it was). */
export const CaseCloseResponse = z.object({ case: CaseRow, undo: UndoTicket });
const dayNumber = (ymd: string): number => Date.UTC(+ymd.slice(0, 4), +ymd.slice(5, 7) - 1, +ymd.slice(8, 10)) / 86_400_000;
export const CASE_MAX_DAYS = 30;
export const CASE_MAX_TARGETS = 50;
export const CaseCreateRequest = z
  .object({
    title: z.string().trim().min(2).max(40),
    targetUserIds: z.array(UserId).min(1).max(CASE_MAX_TARGETS),
    from: Ymd,
    to: Ymd,
    reasonCategory: ReasonCategory,
    description: z.string().trim().min(10).max(500),
  })
  .superRefine((b, ctx) => {
    const span = dayNumber(b.to) - dayNumber(b.from);
    if (span < 0 || span >= CASE_MAX_DAYS) ctx.addIssue({ code: 'custom', message: `period must be from ≤ to and ≤ ${CASE_MAX_DAYS} days`, path: ['to'] });
  });
export const CaseCreateResponse = z.object({ case: CaseRow, approval: ApprovalRow });
export const CaseParams = z.object({ caseId: Id });
/** "조사 중 통지 유예" — needs its own approval; max days from policy; auto-shows after the deadline. */
export const CaseNotifyDeferralRequest = z.object({ until: Ymd, reason: z.string().trim().min(10).max(200) });

/** Service accounts (plan 7.8): owner · purpose · expiry · token (axh_svc_) shown once; gateway calls only. */
export const ServiceAccountRow = z.object({
  id: Id,
  name: z.string(),
  purpose: z.string(),
  owner: PersonRef,
  team: TeamRef.nullable(),
  createdAt: EpochMs,
  expiresAt: EpochMs,
  status: z.enum(['active', 'suspended', 'expired']),
  tokenPrefix: z.string(),
  tokenReady: z.boolean().default(false),
  ownerBlocked: z.boolean().default(false),
  lastUsedAt: EpochMs.nullable(),
  daysLeft: z.int(),
});
export const ServiceAccountListResponse = z.object({ items: z.array(ServiceAccountRow) });
export const ServiceAccountCreateRequest = z.object({
  name: z.string().regex(/^[a-z0-9-]{3,32}$/),
  purpose: z.string().trim().min(1).max(60),
  ownerId: UserId,
  expiresAt: Ymd,
});
export const ServiceAccountUpdateRequest = ServiceAccountCreateRequest;
export const ServiceAccountTokenResponse = z.object({ account: ServiceAccountRow, token: z.string().startsWith('axh_svc_') });
export const ServiceAccountParams = z.object({ accountId: Id });

/** Quarterly review of text/identity/export holders (plan 7.4.2). */
export const AccessReviewResponse = z.object({
  quarter: z.string().regex(/^\d{4}-Q[1-4]$/),
  /** Binds the current quarter, holder/role state and latest review to this reviewer. */
  snapshot: z.string().regex(/^[a-f0-9]{64}$/),
  items: z.array(z.object({ grant: GrantRow, keys: z.array(PermissionKey), lastUsedAt: EpochMs.nullable(), usageKnown: z.boolean().default(false), dormant: z.boolean() })),
  decidedAt: EpochMs.nullable(),
});
export const AccessReviewRequest = z.object({
  quarter: z.string().regex(/^\d{4}-Q[1-4]$/), snapshot: z.string().regex(/^[a-f0-9]{64}$/),
  decisions: z.array(z.object({ grantId: Id, keep: z.boolean() })).min(1), note: z.string().trim().max(200).optional(),
});
export type AccessReviewRequest = z.infer<typeof AccessReviewRequest>;

export const adminRolesSchemas = {
  RoleParams,
  RolePermissionParams,
  RoleView,
  RolesResponse,
  RoleCreateRequest,
  RoleCreateResponse,
  RoleUpdateRequest,
  RolePermissionChangeRequest,
  RolePermissionChangeResponse,
  UndoableOkResponse,
  ApprovalWithdrawResponse,
  CaseCloseResponse,
  ApprovalRow,
  GrantRow,
  GrantListQuery,
  GrantListResponse,
  GrantCandidatesQuery,
  GrantCandidatesResponse,
  GrantPreviewRequest,
  GrantPreviewResponse,
  GrantCreateRequest,
  GrantCreateResponse,
  GrantParams,
  GrantRevokeRequest,
  ApprovalListQuery,
  ApprovalListResponse,
  ApprovalParams,
  ApprovalApproveRequest,
  ApprovalRejectRequest,
  CaseRow,
  CaseListResponse,
  CaseCandidatesQuery,
  CaseCandidatesResponse,
  CaseCreateRequest,
  CaseCreateResponse,
  CaseParams,
  CaseNotifyDeferralRequest,
  ServiceAccountRow,
  ServiceAccountListResponse,
  ServiceAccountCreateRequest,
  ServiceAccountUpdateRequest,
  ServiceAccountTokenResponse,
  ServiceAccountParams,
  AccessReviewResponse,
  AccessReviewRequest,
} as const;
